import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraPersonPicker } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraEyebrow, SpecraQuote, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraNumberedNav, SpecraPopover } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { rfiQuestionHref } from '../../../components/RecordShell/rfiNav';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { QUESTION_STATUS_BADGE, QUESTION_STATUS_LABEL, questionNavTone, rfiQuestionStatus } from './questionStatus';
function PersonField({ canEdit, label, onChange, people, testId, userId }) {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const person = people.find((p)=>p.userId === userId);
    const missing = !userId.trim();
    const name = person?.name || person?.email || (missing ? '' : 'Unknown user');
    const text = missing ? label === 'Owner' ? 'No owner' : 'no reviewer' : `${label} ${name}`;
    if (!canEdit) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            "data-testid": testId,
            size: "meta",
            tone: missing && label === 'Owner' ? 'danger' : 'muted',
            children: text
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                className: missing && label === 'Owner' ? 'text-danger' : 'text-muted-foreground',
                "data-testid": testId,
                onClick: ()=>setOpen((o)=>!o),
                ref: setAnchor,
                size: "inline",
                variant: "link",
                children: text
            }),
            /*#__PURE__*/ _jsx(SpecraPopover, {
                anchor: anchor,
                "aria-label": `Change ${label.toLowerCase()}`,
                onOpenChange: setOpen,
                open: open,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-72",
                    children: /*#__PURE__*/ _jsx(SpecraPersonPicker, {
                        label: label === 'Owner' ? 'Owner' : 'Reviewer',
                        onPersonChange: (p)=>{
                            if (p) onChange(p.id);
                            setOpen(false);
                        },
                        people: people.map((p)=>({
                                id: p.userId,
                                name: p.name || p.email,
                                email: p.email
                            })),
                        placeholder: "Choose a person",
                        value: userId || undefined
                    })
                })
            })
        ]
    });
}
/**
 * The question head (F14): question squares, "Question n · Attachment A, §2",
 * status, owner and reviewer, and the verbatim government question.
 */ export function RfiQuestionHead({ active, canEdit, onAssign, people, routeId, sections, source }) {
    const n = sections.findIndex((s)=>s.id === active.id) + 1;
    const status = rfiQuestionStatus(active);
    const ref = source ? [
        source.documentReference,
        source.section ? `§${source.section}` : ''
    ].filter(Boolean).join(', ') : '';
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-3)]",
        "data-testid": Testids.rfiWorkspace.questionHead,
        children: [
            /*#__PURE__*/ _jsx(SpecraNumberedNav, {
                activeId: active.id,
                "data-testid": Testids.rfiWorkspace.questionNav,
                getItemTestId: ()=>Testids.rfiWorkspace.questionNavItem,
                items: sections.map((s, i)=>({
                        id: s.id,
                        label: String(i + 1),
                        title: `Question ${i + 1}: ${QUESTION_STATUS_LABEL[rfiQuestionStatus(s)]}`,
                        tone: questionNavTone(s),
                        href: rfiQuestionHref(routeId, s.id)
                    })),
                label: "Questions",
                leadingLabel: "Questions",
                renderLink: renderRouterLink
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEyebrow, {
                        children: `Question ${n}${ref ? ` · ${ref}` : ''}`
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        "data-testid": Testids.rfiWorkspace.questionStatus,
                        variant: QUESTION_STATUS_BADGE[status],
                        children: QUESTION_STATUS_LABEL[status]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "span",
                        className: "flex flex-wrap items-center gap-[var(--space-1)]",
                        size: "meta",
                        tone: "muted",
                        children: [
                            /*#__PURE__*/ _jsx(PersonField, {
                                canEdit: canEdit,
                                label: "Owner",
                                onChange: (id)=>onAssign({
                                        ownerUserId: id
                                    }),
                                people: people,
                                testId: Testids.rfiWorkspace.questionOwner,
                                userId: active.ownerUserId
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: "·"
                            }),
                            /*#__PURE__*/ _jsx(PersonField, {
                                canEdit: canEdit,
                                label: "reviewer",
                                onChange: (id)=>onAssign({
                                        reviewerUserId: id
                                    }),
                                people: people,
                                testId: Testids.rfiWorkspace.questionReviewer,
                                userId: active.reviewerUserId
                            })
                        ]
                    })
                ]
            }),
            source?.originalText ? /*#__PURE__*/ _jsx(SpecraQuote, {
                "data-testid": Testids.rfiWorkspace.questionQuote,
                size: "md",
                children: `“${source.originalText}”`
            }) : null
        ]
    });
}
